import type { Coverage } from "./energy.js";

export type SeriesKey = "solar" | "grid" | "charge" | "discharge" | "house";

/**
 * The flows drawn, each told apart by its dash as well as its colour, so the
 * key is not carried by colour alone (docs/REQUIREMENTS.md section 4).
 *
 * The battery's two flows share its green, darker for charging, and differ by
 * dash (docs/DECISIONS/0032). The design has no sixth hue that holds 3:1 on a
 * card in both themes, and in dark mode the two greens are one token, so the
 * dash is what separates them there.
 *
 * Lines are drawn with round caps, which lengthen every dash by the stroke
 * width and shorten every gap by the same. A gap no wider than the stroke
 * closes, and the line draws solid: "4 2 1 2" did exactly that.
 */
export const SERIES: readonly {
  key: SeriesKey;
  label: string;
  stroke: string;
  fill: string;
  ring: string;
  dash?: string;
}[] = [
  {
    key: "solar",
    label: "Solar",
    stroke: "stroke-accent",
    fill: "bg-accent",
    ring: "border-accent",
  },
  {
    key: "grid",
    label: "From the grid",
    stroke: "stroke-ink",
    fill: "bg-ink",
    ring: "border-ink",
    dash: "5 3",
  },
  {
    key: "charge",
    label: "Into the battery",
    stroke: "stroke-accent-2-ink",
    fill: "bg-accent-2-ink",
    ring: "border-accent-2-ink",
    dash: "6 4 1 4",
  },
  {
    key: "discharge",
    label: "From the battery",
    stroke: "stroke-accent-2",
    fill: "bg-accent-2",
    ring: "border-accent-2",
    dash: "1.5 3",
  },
  {
    key: "house",
    label: "Delivered to the house",
    stroke: "stroke-ink-muted",
    fill: "bg-ink-muted",
    ring: "border-ink-muted",
    dash: "8 4",
  },
];

export interface ChartBucket {
  /** The x label this bucket sits under, repeated across a group. */
  group: string;
  /** A shorter form of `group` for phone widths, where a year of labels must share 240px. */
  narrow?: string;
  /** Every figure for the bucket, in words: the tooltip and the text equivalent. */
  label: string;
  /** How much of the bucket's period was recorded. A partial one draws hollow points. */
  coverage: Coverage;
  /** kWh per flow; null where nothing was recorded, which breaks the line. */
  values: Record<SeriesKey, number | null>;
}

export interface ChartData {
  buckets: readonly ChartBucket[];
  /** Each flow across the whole range, as the server totalled it. */
  totals: Record<SeriesKey, number | null>;
  /** How much of the range is missing, or null when none of it is. */
  note: string | null;
}

/** Runs of consecutive recorded values; a null ends one, so no line crosses a gap. */
export function segments(values: readonly (number | null)[]): { index: number; value: number }[][] {
  const runs: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (current.length > 0) runs.push(current);
      current = [];
      return;
    }
    current.push({ index, value });
  });
  if (current.length > 0) runs.push(current);
  return runs;
}
